import { useEffect, useRef, useState } from "react";

type Options = {
    delay?: number;
    minimumDuration?: number;
};

export function useSkeleton(
    isLoading: boolean,
    { delay = 150, minimumDuration = 300 }: Options = {}
) {
    const [showSkeleton, setShowSkeleton] = useState(false);

    const showTimeout = useRef<number | null>(null);
    const hideTimeout = useRef<number | null>(null);
    const shownAt = useRef<number | null>(null);

    useEffect(() => {
        if (isLoading) {
            // Start timer before showing loader
            showTimeout.current = window.setTimeout(() => {
                shownAt.current = Date.now();
                setShowSkeleton(true);
            }, delay);

            return;
        }

        // Loading finished before loader became visible
        if (showTimeout.current !== null) {
            clearTimeout(showTimeout.current);
            showTimeout.current = null;
        }

        // Loader was never shown
        if (!showSkeleton || shownAt.current === null) {
            setShowSkeleton(false);
            return;
        }

        const visibleFor = Date.now() - shownAt.current;
        const remaining = Math.max(0, minimumDuration - visibleFor);

        hideTimeout.current = window.setTimeout(() => {
            shownAt.current = null;
            setShowSkeleton(false);
        }, remaining);

        return () => {
            if (hideTimeout.current !== null) {
                clearTimeout(hideTimeout.current);
            }
        };
    }, [isLoading, showSkeleton, delay, minimumDuration]);

    useEffect(() => {
        return () => {
            if (showTimeout.current !== null) {
                clearTimeout(showTimeout.current);
            }

            if (hideTimeout.current !== null) {
                clearTimeout(hideTimeout.current);
            }
        };
    }, []);

    const showContent = !isLoading && !showSkeleton;

    return {
        showSkeleton: showSkeleton,
        showContent: showContent,
        showLoading: !showContent && !showSkeleton
    };
}
